/** Kerbside side-facility tags on motor roads (Model A) — see ROAD_LANE_DIRECTION_POLICY.md §2.13 */

export type SidePlacement = '' | 'none' | 'left' | 'right' | 'both';

/** Divided strips: physical placement relative to divide (not trace left/right). */
export type StripKerbPlacement = '' | 'none' | 'inner' | 'outer';
export type AccessTag = '' | 'yes' | 'no' | 'designated';
export type SideFacilityCondition = '' | 'good' | 'fair' | 'poor';
/** @deprecated use SideFacilityCondition */
export type SidewalkCondition = SideFacilityCondition;
export type GutterTag = '' | 'yes' | 'no' | 'open' | 'covered';

export type GeometryFacilityKey = 'sidewalk' | 'cycleway' | 'shoulder' | 'parking_lane';
export type FacilityPlacementKey = 'sidewalk' | 'cycleway' | 'shoulder' | 'parkingLane';

/** Chainage segment along the parent trace — ratios 0–1, no separate map endpoints needed. */
export interface SideFacilitySegment {
  side: 'left' | 'right';
  start: string;
  end: string;
}

export type SideFacilitySegmentMap = Partial<Record<GeometryFacilityKey, SideFacilitySegment[]>>;

/** Model B — trace the facility centreline as its own road contribution. */
export const STANDALONE_KERBSIDE_PATH_CATEGORIES = new Set(['shoulder', 'parking_lane']);

/** Road types that support kerbside side-facility tags (not separate path types). */
export const MOTOR_ROAD_CATEGORIES = new Set([
  'motorway',
  'motorway_link',
  'trunk',
  'primary',
  'primary_link',
  'secondary',
  'secondary_link',
  'tertiary',
  'tertiary_link',
  'unclassified',
  'residential',
  'living_street',
  'service',
  'construction',
]);

export const SIDE_PLACEMENT_OPTIONS: { value: SidePlacement; label: string }[] = [
  { value: '', label: 'Not surveyed' },
  { value: 'none', label: 'None' },
  { value: 'left', label: 'Left of draw direction' },
  { value: 'right', label: 'Right of draw direction' },
  { value: 'both', label: 'Both sides' },
];

export const DIVIDED_KERB_PLACEMENT_OPTIONS: { value: StripKerbPlacement; label: string }[] = [
  { value: '', label: 'Not surveyed' },
  { value: 'none', label: 'None' },
  { value: 'inner', label: 'Inner — divide side' },
  { value: 'outer', label: 'Outer — property side' },
];

export const ACCESS_TAG_OPTIONS: { value: AccessTag; label: string }[] = [
  { value: '', label: 'Not surveyed' },
  { value: 'yes', label: 'Yes / allowed' },
  { value: 'no', label: 'No / prohibited' },
  { value: 'designated', label: 'Designated' },
];

export const SIDE_FACILITY_CONDITION_OPTIONS: { value: SideFacilityCondition; label: string }[] = [
  { value: '', label: 'Not surveyed' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];
/** @deprecated use SIDE_FACILITY_CONDITION_OPTIONS */
export const SIDEWALK_CONDITION_OPTIONS = SIDE_FACILITY_CONDITION_OPTIONS;

export const GUTTER_OPTIONS: { value: GutterTag; label: string }[] = [
  { value: '', label: 'Not surveyed' },
  { value: 'yes', label: 'Yes — drainage present' },
  { value: 'no', label: 'No gutter' },
  { value: 'open', label: 'Open gutter (exposed)' },
  { value: 'covered', label: 'Covered / channelled' },
];

/** Form order: property line → kerb (standard urban layout). Tile offsets use backend stack order. */
export const KERBSIDE_FORM_DISPLAY_ORDER = [
  'sidewalk',
  'cycleway',
  'parking_lane',
  'shoulder',
] as const satisfies readonly GeometryFacilityKey[];

export const CARRIAGEWAY_SIDE_PLACEMENT_FIELDS = [
  { stateKey: 'sidewalk', dividedKey: 'Sidewalk', label: 'Sidewalk', extra: 'sidewalk' as GeometryFacilityKey },
  { stateKey: 'cycleway', dividedKey: 'Cycleway', label: 'Cycle lane', extra: 'cycleway' as GeometryFacilityKey },
  { stateKey: 'parkingLane', dividedKey: 'ParkingLane', label: 'Parking lane', extra: 'parking_lane' as GeometryFacilityKey },
  { stateKey: 'shoulder', dividedKey: 'Shoulder', label: 'Shoulder', extra: 'shoulder' as GeometryFacilityKey },
] as const;

export const FACILITY_DIMENSION_FIELDS: {
  placementKey: FacilityPlacementKey;
  geometryKey: GeometryFacilityKey;
  label: string;
  widthKey: keyof SideFacilityFormState;
  kerbKey: keyof SideFacilityFormState;
  conditionKey: keyof SideFacilityFormState;
  conditionExtraKey: string;
  maxspeedKey: keyof SideFacilityFormState;
  maxspeedExtraKey: string;
  dividedAWidthKey: keyof SideFacilityFormState;
  dividedAKerbKey: keyof SideFacilityFormState;
  dividedBWidthKey: keyof SideFacilityFormState;
  dividedBKerbKey: keyof SideFacilityFormState;
  defaultWidth: string;
}[] = [
  {
    placementKey: 'sidewalk',
    geometryKey: 'sidewalk',
    label: 'Sidewalk',
    widthKey: 'sidewalkWidth',
    kerbKey: 'sidewalkKerbGap',
    conditionKey: 'sidewalkCondition',
    conditionExtraKey: 'sidewalk_condition',
    maxspeedKey: 'sidewalkMaxspeed',
    maxspeedExtraKey: 'sidewalk_maxspeed',
    dividedAWidthKey: 'carriagewayASidewalkWidth',
    dividedAKerbKey: 'carriagewayASidewalkKerbGap',
    dividedBWidthKey: 'carriagewayBSidewalkWidth',
    dividedBKerbKey: 'carriagewayBSidewalkKerbGap',
    defaultWidth: '1.5',
  },
  {
    placementKey: 'cycleway',
    geometryKey: 'cycleway',
    label: 'Cycle lane',
    widthKey: 'cyclewayWidth',
    kerbKey: 'cyclewayKerbGap',
    conditionKey: 'cyclewayCondition',
    conditionExtraKey: 'cycleway_condition',
    maxspeedKey: 'cyclewayMaxspeed',
    maxspeedExtraKey: 'cycleway_maxspeed',
    dividedAWidthKey: 'carriagewayACyclewayWidth',
    dividedAKerbKey: 'carriagewayACyclewayKerbGap',
    dividedBWidthKey: 'carriagewayBCyclewayWidth',
    dividedBKerbKey: 'carriagewayBCyclewayKerbGap',
    defaultWidth: '1.5',
  },
  {
    placementKey: 'parkingLane',
    geometryKey: 'parking_lane',
    label: 'Parking lane',
    widthKey: 'parkingLaneWidth',
    kerbKey: 'parkingLaneKerbGap',
    conditionKey: 'parkingLaneCondition',
    conditionExtraKey: 'parking_lane_condition',
    maxspeedKey: 'parkingLaneMaxspeed',
    maxspeedExtraKey: 'parking_lane_maxspeed',
    dividedAWidthKey: 'carriagewayAParkingLaneWidth',
    dividedAKerbKey: 'carriagewayAParkingLaneKerbGap',
    dividedBWidthKey: 'carriagewayBParkingLaneWidth',
    dividedBKerbKey: 'carriagewayBParkingLaneKerbGap',
    defaultWidth: '2.0',
  },
  {
    placementKey: 'shoulder',
    geometryKey: 'shoulder',
    label: 'Shoulder',
    widthKey: 'shoulderWidth',
    kerbKey: 'shoulderKerbGap',
    conditionKey: 'shoulderCondition',
    conditionExtraKey: 'shoulder_condition',
    maxspeedKey: 'shoulderMaxspeed',
    maxspeedExtraKey: 'shoulder_maxspeed',
    dividedAWidthKey: 'carriagewayAShoulderWidth',
    dividedAKerbKey: 'carriagewayAShoulderKerbGap',
    dividedBWidthKey: 'carriagewayBShoulderWidth',
    dividedBKerbKey: 'carriagewayBShoulderKerbGap',
    defaultWidth: '1.0',
  },
];

export type CarriagewaySide = 'a' | 'b';

export interface SideFacilityFormState {
  sidewalk: SidePlacement;
  cycleway: SidePlacement;
  shoulder: SidePlacement;
  parkingLane: SidePlacement;
  foot: AccessTag;
  bicycle: AccessTag;
  bus: AccessTag;
  psv: AccessTag;
  access: AccessTag;
  sidewalkCondition: SideFacilityCondition;
  cyclewayCondition: SideFacilityCondition;
  shoulderCondition: SideFacilityCondition;
  parkingLaneCondition: SideFacilityCondition;
  sidewalkMaxspeed: string;
  cyclewayMaxspeed: string;
  shoulderMaxspeed: string;
  parkingLaneMaxspeed: string;
  gutter: GutterTag;
  sidewalkWidth: string;
  cyclewayWidth: string;
  shoulderWidth: string;
  parkingLaneWidth: string;
  sidewalkKerbGap: string;
  cyclewayKerbGap: string;
  shoulderKerbGap: string;
  parkingLaneKerbGap: string;
  carriagewayASidewalkWidth: string;
  carriagewayACyclewayWidth: string;
  carriagewayAShoulderWidth: string;
  carriagewayAParkingLaneWidth: string;
  carriagewayBSidewalkWidth: string;
  carriagewayBCyclewayWidth: string;
  carriagewayBShoulderWidth: string;
  carriagewayBParkingLaneWidth: string;
  carriagewayASidewalkKerbGap: string;
  carriagewayACyclewayKerbGap: string;
  carriagewayAShoulderKerbGap: string;
  carriagewayAParkingLaneKerbGap: string;
  carriagewayBSidewalkKerbGap: string;
  carriagewayBCyclewayKerbGap: string;
  carriagewayBShoulderKerbGap: string;
  carriagewayBParkingLaneKerbGap: string;
  sideFacilitySegments: SideFacilitySegmentMap;
  carriagewayASegments: SideFacilitySegmentMap;
  carriagewayBSegments: SideFacilitySegmentMap;
  carriagewayASidewalk: StripKerbPlacement;
  carriagewayACycleway: StripKerbPlacement;
  carriagewayAShoulder: StripKerbPlacement;
  carriagewayAParkingLane: StripKerbPlacement;
  carriagewayBSidewalk: StripKerbPlacement;
  carriagewayBCycleway: StripKerbPlacement;
  carriagewayBShoulder: StripKerbPlacement;
  carriagewayBParkingLane: StripKerbPlacement;
}

export const EMPTY_SIDE_FACILITIES: SideFacilityFormState = {
  sidewalk: '',
  cycleway: '',
  shoulder: '',
  parkingLane: '',
  foot: '',
  bicycle: '',
  bus: '',
  psv: '',
  access: '',
  sidewalkCondition: '',
  cyclewayCondition: '',
  shoulderCondition: '',
  parkingLaneCondition: '',
  sidewalkMaxspeed: '',
  cyclewayMaxspeed: '',
  shoulderMaxspeed: '',
  parkingLaneMaxspeed: '',
  gutter: '',
  sidewalkWidth: '',
  cyclewayWidth: '',
  shoulderWidth: '',
  parkingLaneWidth: '',
  sidewalkKerbGap: '',
  cyclewayKerbGap: '',
  shoulderKerbGap: '',
  parkingLaneKerbGap: '',
  carriagewayASidewalkWidth: '',
  carriagewayACyclewayWidth: '',
  carriagewayAShoulderWidth: '',
  carriagewayAParkingLaneWidth: '',
  carriagewayBSidewalkWidth: '',
  carriagewayBCyclewayWidth: '',
  carriagewayBShoulderWidth: '',
  carriagewayBParkingLaneWidth: '',
  carriagewayASidewalkKerbGap: '',
  carriagewayACyclewayKerbGap: '',
  carriagewayAShoulderKerbGap: '',
  carriagewayAParkingLaneKerbGap: '',
  carriagewayBSidewalkKerbGap: '',
  carriagewayBCyclewayKerbGap: '',
  carriagewayBShoulderKerbGap: '',
  carriagewayBParkingLaneKerbGap: '',
  sideFacilitySegments: {},
  carriagewayASegments: {},
  carriagewayBSegments: {},
  carriagewayASidewalk: '',
  carriagewayACycleway: '',
  carriagewayAShoulder: '',
  carriagewayAParkingLane: '',
  carriagewayBSidewalk: '',
  carriagewayBCycleway: '',
  carriagewayBShoulder: '',
  carriagewayBParkingLane: '',
};

export function isStandaloneKerbsidePathCategory(category: string | null | undefined): boolean {
  if (!category) return false;
  return STANDALONE_KERBSIDE_PATH_CATEGORIES.has(category);
}

export function supportsSideFacilityTags(category: string | null | undefined): boolean {
  if (!category) return false;
  return MOTOR_ROAD_CATEGORIES.has(category);
}

export function parseSidePlacement(raw: unknown): SidePlacement {
  if (raw == null || raw === '') return '';
  const v = String(raw).trim().toLowerCase();
  if (v === 'yes') return 'both';
  if (v === 'no' || v === 'separate') return 'none';
  if (v === 'left' || v === 'right' || v === 'both' || v === 'none') return v;
  return '';
}

export function parseStripKerbPlacement(raw: unknown): StripKerbPlacement {
  if (raw == null || raw === '') return '';
  const v = String(raw).trim().toLowerCase();
  if (v === 'no' || v === 'separate') return 'none';
  if (v === 'inner' || v === 'outer' || v === 'none') return v;
  return '';
}

export function parseAccessTag(raw: unknown): AccessTag {
  if (raw == null || raw === '') return '';
  const v = String(raw).trim().toLowerCase();
  if (v === 'yes' || v === 'no' || v === 'designated') return v;
  return '';
}

export function parseSideFacilityCondition(raw: unknown): SideFacilityCondition {
  if (raw == null || raw === '') return '';
  const v = String(raw).trim().toLowerCase();
  if (v === 'good' || v === 'fair' || v === 'poor') return v;
  return '';
}

/** @deprecated use parseSideFacilityCondition */
export const parseSidewalkCondition = parseSideFacilityCondition;

export function parseGutterTag(raw: unknown): GutterTag {
  if (raw == null || raw === '') return '';
  const v = String(raw).trim().toLowerCase();
  if (v === 'yes' || v === 'no' || v === 'open' || v === 'covered') return v;
  return '';
}

export function carriagewaySideFieldKey(
  side: 'A' | 'B',
  dividedKey: (typeof CARRIAGEWAY_SIDE_PLACEMENT_FIELDS)[number]['dividedKey'],
): keyof SideFacilityFormState {
  return `carriageway${side}${dividedKey}` as keyof SideFacilityFormState;
}

function sideForSubmit(value: SidePlacement | StripKerbPlacement): string | undefined {
  if (!value) return undefined;
  return value;
}

function parseFacilityWidthM(raw: string, max = 10): number | null {
  if (!raw.trim()) return null;
  const n = parseFloat(raw.trim());
  if (Number.isNaN(n) || n <= 0 || n > max) return null;
  return n;
}

function isActiveSidePlacement(value: SidePlacement | StripKerbPlacement): boolean {
  return value === 'left' || value === 'right' || value === 'both' || value === 'inner' || value === 'outer';
}

const PLACEMENT_FIELD_MAP: Record<
  FacilityPlacementKey,
  { single: keyof SideFacilityFormState; dividedA: keyof SideFacilityFormState; dividedB: keyof SideFacilityFormState }
> = {
  sidewalk: { single: 'sidewalk', dividedA: 'carriagewayASidewalk', dividedB: 'carriagewayBSidewalk' },
  cycleway: { single: 'cycleway', dividedA: 'carriagewayACycleway', dividedB: 'carriagewayBCycleway' },
  shoulder: { single: 'shoulder', dividedA: 'carriagewayAShoulder', dividedB: 'carriagewayBShoulder' },
  parkingLane: { single: 'parkingLane', dividedA: 'carriagewayAParkingLane', dividedB: 'carriagewayBParkingLane' },
};

export function placementForScope(
  state: SideFacilityFormState,
  divided: boolean,
  strip: 'A' | 'B' | null,
  facility: FacilityPlacementKey,
): SidePlacement | StripKerbPlacement {
  const fields = PLACEMENT_FIELD_MAP[facility];
  if (!divided || !strip) return state[fields.single] as SidePlacement;
  return state[strip === 'A' ? fields.dividedA : fields.dividedB] as StripKerbPlacement;
}

export function hasActiveFacilityPlacement(
  divided: boolean,
  state: SideFacilityFormState,
  facility: FacilityPlacementKey,
): boolean {
  if (!divided) {
    return isActiveSidePlacement(state[PLACEMENT_FIELD_MAP[facility].single] as SidePlacement);
  }
  const fields = PLACEMENT_FIELD_MAP[facility];
  return isActiveSidePlacement(state[fields.dividedA] as SidePlacement)
    || isActiveSidePlacement(state[fields.dividedB] as SidePlacement);
}

export function hasActiveSidewalkPlacement(divided: boolean, state: SideFacilityFormState): boolean {
  return hasActiveFacilityPlacement(divided, state, 'sidewalk');
}

export function facilityDimensionsForScope(
  _state: SideFacilityFormState,
  divided: boolean,
  strip: 'A' | 'B' | null,
  field: (typeof FACILITY_DIMENSION_FIELDS)[number],
): { widthKey: keyof SideFacilityFormState; kerbKey: keyof SideFacilityFormState } {
  if (!divided || !strip) {
    return { widthKey: field.widthKey, kerbKey: field.kerbKey };
  }
  if (strip === 'A') {
    return { widthKey: field.dividedAWidthKey, kerbKey: field.dividedAKerbKey };
  }
  return { widthKey: field.dividedBWidthKey, kerbKey: field.dividedBKerbKey };
}

export function segmentMapForScope(
  state: SideFacilityFormState,
  divided: boolean,
  strip: 'A' | 'B' | null,
): SideFacilitySegmentMap {
  if (!divided || !strip) return state.sideFacilitySegments;
  return strip === 'A' ? state.carriagewayASegments : state.carriagewayBSegments;
}

function parseSegmentRatio(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0 || n > 1) return null;
  return n;
}

/** Validate optional partial segments — empty map means full-length coverage. */
export function validateSideFacilitySegments(
  segments: SideFacilitySegmentMap | undefined,
  facilityLabel: string,
): string | null {
  if (!segments) return null;
  for (const [, entries] of Object.entries(segments)) {
    if (!entries?.length) continue;
    for (const entry of entries) {
      const start = parseSegmentRatio(entry.start);
      const end = parseSegmentRatio(entry.end);
      if (start == null || end == null) {
        return `${facilityLabel}: enter start/end as decimals from 0 to 1 along your trace.`;
      }
      if (end <= start) {
        return `${facilityLabel}: segment end must be greater than start.`;
      }
    }
  }
  return null;
}

function serializeSegments(map: SideFacilitySegmentMap | undefined): Record<string, unknown> | undefined {
  if (!map) return undefined;
  const out: Record<string, unknown> = {};
  for (const [facilityKey, entries] of Object.entries(map)) {
    if (!entries?.length) continue;
    const cleaned = entries
      .map((entry) => {
        const start = parseSegmentRatio(entry.start);
        const end = parseSegmentRatio(entry.end);
        if (start == null || end == null || end <= start) return null;
        return { side: entry.side, start, end };
      })
      .filter(Boolean);
    if (cleaned.length) out[facilityKey] = cleaned;
  }
  return Object.keys(out).length ? out : undefined;
}

export function parseSideFacilitySegmentsFromExtra(raw: unknown): SideFacilitySegmentMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: SideFacilitySegmentMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    const geometryKey = key as GeometryFacilityKey;
    out[geometryKey] = value
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const side = String((entry as { side?: string }).side ?? '').toLowerCase();
        if (side !== 'left' && side !== 'right') return null;
        const start = (entry as { start?: unknown }).start;
        const end = (entry as { end?: unknown }).end;
        return {
          side,
          start: start == null ? '' : String(start),
          end: end == null ? '' : String(end),
        };
      })
      .filter(Boolean) as SideFacilitySegment[];
  }
  return out;
}

/** Block submit when motor-road width or tagged facility dimensions are missing. */
export function validateSideFacilityFields(payload: {
  category: string;
  divided: boolean;
  state: SideFacilityFormState;
  roadWidth: string;
  carriagewayAWidth?: string;
  carriagewayBWidth?: string;
}): string | null {
  if (!supportsSideFacilityTags(payload.category)) return null;

  if (!payload.divided) {
    if (parseFacilityWidthM(payload.roadWidth, 60) == null) {
      return 'Enter road width (metres) for this carriageway.';
    }
  } else {
    const aWidth = parseFacilityWidthM(payload.carriagewayAWidth ?? '', 60);
    const bWidth = parseFacilityWidthM(payload.carriagewayBWidth ?? '', 60);
    if (aWidth == null || bWidth == null) {
      return 'Enter paved widths (metres) for carriageways A and B.';
    }
  }

  const scopes: Array<{ divided: boolean; strip: 'A' | 'B' | null }> = payload.divided
    ? [{ divided: true, strip: 'A' }, { divided: true, strip: 'B' }]
    : [{ divided: false, strip: null }];

  for (const scope of scopes) {
    for (const field of FACILITY_DIMENSION_FIELDS) {
      const placement = placementForScope(payload.state, scope.divided, scope.strip, field.placementKey);
      if (!isActiveSidePlacement(placement)) continue;
      const { widthKey } = facilityDimensionsForScope(payload.state, scope.divided, scope.strip, field);
      if (parseFacilityWidthM(String(payload.state[widthKey] ?? ''), 10) == null) {
        const stripLabel = scope.strip ? `carriageway ${scope.strip}` : 'this carriageway';
        return `Enter ${field.label.toLowerCase()} width (metres) on ${stripLabel}.`;
      }
    }

    const segMap = segmentMapForScope(payload.state, scope.divided, scope.strip);
    const segErr = validateSideFacilitySegments(segMap, scope.strip ? `Carriageway ${scope.strip}` : 'Side facility');
    if (segErr) return segErr;
  }

  return null;
}

function appendNumericExtras(
  extra: Record<string, unknown>,
  state: SideFacilityFormState,
  divided: boolean,
  strip: 'A' | 'B' | null,
) {
  for (const field of FACILITY_DIMENSION_FIELDS) {
    if (!hasActiveFacilityPlacement(divided, state, field.placementKey)) continue;
    const { widthKey, kerbKey } = facilityDimensionsForScope(state, divided, strip, field);
    const widthRaw = String(state[widthKey] ?? '').trim();
    const kerbRaw = String(state[kerbKey] ?? '').trim();
    const width = parseFloat(widthRaw);
    const kerb = parseFloat(kerbRaw);
    const prefix = divided && strip ? `carriageway_${strip.toLowerCase()}_` : '';
    if (!Number.isNaN(width) && width > 0 && width <= 10) {
      extra[`${prefix}${field.geometryKey}_width`] = width;
    }
    if (!Number.isNaN(kerb) && kerb >= 0 && kerb <= 5) {
      extra[`${prefix}${field.geometryKey}_kerb_gap`] = kerb;
    }
  }
}

export function sideFacilityExtras(
  category: string,
  divided: boolean,
  state: SideFacilityFormState,
): Record<string, unknown> {
  if (!supportsSideFacilityTags(category)) return {};

  const extra: Record<string, unknown> = {};

  if (divided) {
    for (const strip of ['a', 'b'] as const) {
      const stripUpper = strip === 'a' ? 'A' : 'B';
      for (const field of CARRIAGEWAY_SIDE_PLACEMENT_FIELDS) {
        const value = sideForSubmit(
          state[carriagewaySideFieldKey(stripUpper, field.dividedKey)] as StripKerbPlacement,
        );
        if (value) extra[`carriageway_${strip}_${field.extra}`] = value;
      }
      appendNumericExtras(extra, state, true, stripUpper);
      const segments = serializeSegments(strip === 'a' ? state.carriagewayASegments : state.carriagewayBSegments);
      if (segments) extra[`carriageway_${strip}_side_facility_segments`] = segments;
    }
  } else {
    const sw = sideForSubmit(state.sidewalk);
    const cy = sideForSubmit(state.cycleway);
    const sh = sideForSubmit(state.shoulder);
    const pk = sideForSubmit(state.parkingLane);
    if (sw) extra.sidewalk = sw;
    if (cy) extra.cycleway = cy;
    if (sh) extra.shoulder = sh;
    if (pk) extra.parking_lane = pk;
    appendNumericExtras(extra, state, false, null);
    const segments = serializeSegments(state.sideFacilitySegments);
    if (segments) extra.side_facility_segments = segments;
  }

  for (const [stateKey, extraKey] of [
    ['foot', 'foot'],
    ['bicycle', 'bicycle'],
    ['bus', 'bus'],
    ['psv', 'psv'],
    ['access', 'access'],
  ] as const) {
    const value = parseAccessTag(state[stateKey]);
    if (value) extra[extraKey] = value;
  }

  for (const field of FACILITY_DIMENSION_FIELDS) {
    if (!hasActiveFacilityPlacement(divided, state, field.placementKey)) continue;
    const condition = parseSideFacilityCondition(state[field.conditionKey]);
    if (condition) extra[field.conditionExtraKey] = condition;
    const maxspeedRaw = String(state[field.maxspeedKey] ?? '').trim();
    if (maxspeedRaw) {
      const maxspeed = parseInt(maxspeedRaw, 10);
      if (!Number.isNaN(maxspeed) && maxspeed >= 5 && maxspeed <= 130) {
        extra[field.maxspeedExtraKey] = maxspeed;
      }
    }
  }

  const gutter = parseGutterTag(state.gutter);
  if (gutter) extra.gutter = gutter;

  return extra;
}
